import { MarkdownImage } from '@/components/common/MarkdownImage'
import { MarkdownTextDivider } from '@/components/common/MarkdownTextDivider'
import { isAnchorImage, isImage } from '@/lib/markdown'
import { css } from '@emotion/react'
import Markdown, { Options } from 'markdown-to-jsx'
import { colors, font, mq } from 'plume-ui'
import AnchorLink from 'react-anchor-link-smooth-scroll'

const paddingSm = 24
const paddingLg = 48

const listMarkerPadding = 25

export const paddingSmartphoneStyle = css`
  padding-left: ${paddingSm}px;
  padding-right: ${paddingSm}px;
`

export const paddingDesktopStyle = css`
  padding-left: ${paddingLg}px;
  padding-right: ${paddingLg}px;
`

const bodyMdStyle = css`
  & img {
    width: 100vw;
  }

  ${mq.large} {
    & img {
      width: 100%;
    }
  }

  & p {
    font-size: 15px;
    line-height: 1.75;
    letter-spacing: 0.02em;
    color: ${colors.black30};

    ${mq.large} {
      margin: 32px 0;
      font-size: 16px;
      line-height: 2;
    }
  }

  & h1 {
    line-height: 1.3;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      ${paddingDesktopStyle};
    }
  }

  & h2 {
    font-size: 18px;
    margin-top: 48px;
    margin-bottom: 16px;
    line-height: 1.5;
    letter-spacing: 0.02em;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      margin-top: 64px;
      font-size: 20px;
      line-height: 1.75;
      ${paddingDesktopStyle};
    }
  }

  & div + pre {
    margin-top: -15px;
  }
`

type BodyMarkdownProps = {
  shrink: boolean
  options?: Options
  children: React.ReactNode
}

export const BodyMarkdown: React.FC<BodyMarkdownProps> = (props) => {
  return (
    <Markdown
      css={css`
        ${bodyMdStyle};
        margin-top: ${props.shrink ? '16px' : '60px'};
        margin-bottom: 40px;
        ${mq.large} {
          margin-bottom: 64px;
        }

        p {
          ${paddingSmartphoneStyle};

          ${mq.large} {
            ${paddingDesktopStyle};
          }
        }

        ul {
          margin: 16px 0;
          padding-left: calc(${paddingSm}px + ${listMarkerPadding}px);
          padding-right: ${paddingSm}px;

          ${mq.large} {
            padding-left: calc(${paddingLg}px + ${listMarkerPadding}px);
            padding-right: ${paddingLg}px;
          }

          /* nested list style */
          ul {
            padding-left: ${paddingSm}px !important;
            padding-right: 0;

            ${mq.large} {children: React.ReactNode
              padding-left: ${paddingLg}px !important;
              padding-right: 0;
            }
          }

          li {
            list-style: initial;
            line-height: 2;
            letter-spacing: 0.02em;
          }
        }
      `}
      options={{
        ...props.options,
        overrides: {
          a: Link,
          code: Annotation,
          blockquote: BodyFootnote,
          iframe: IFrame,
          h2: Heading,
          p: PorDiv,
          img: MarkdownImage,
          Image: MarkdownImage,
          hr: MarkdownTextDivider,
          ...props.options?.overrides,
        },
      }}
    >
      {props.children}
    </Markdown>
  )
}

type AnnotationProps = {
  children: React.ReactNode
}

export const Annotation: React.FC<AnnotationProps> = (props) => {
  return (
    <span
      css={css`
        display: block;
        color: ${colors.textLight};
        font-size: 12px;
        line-height: 1.5;
        letter-spacing: 0.02em;
        ${mq.large} {
          font-size: 14px;
          line-height: 1.75;
        }
      `}
    >
      {props.children}
    </span>
  )
}

type BodyFootnoteProps = {
  children: React.ReactNode
}

const BodyFootnote: React.FC<BodyFootnoteProps> = (props) => {
  return (
    <pre
      css={css`
        && {
          margin: 8px 24px 0;
          white-space: pre-line;
          word-break: break-all;
          font-family: ${font.family};
          text-align: center;

          p {
            color: ${colors.textLight};
            padding: 0;
            margin: 0;
            font-size: 12px;
            line-height: 1.75;
            letter-spacing: 0.02em;

            & a {
              color: ${colors.link};
              font-weight: ${font.weight.normal};
              border-bottom: none;
              padding-bottom: 0;
              margin: 0;
            }
          }

          ${mq.large} {
            margin: 8px 48px 0;
            p {
              font-size: 14px;
            }
          }
        }
      `}
    >
      {props.children}
    </pre>
  )
}

export const PorDiv = (props: any) => {
  return props.children.some(
    (node: any) => isImage(node) || isAnchorImage(node)
  ) ? (
    <div
      css={css`
        & > img {
          display: block;
        }
        & > a {
          border-bottom: none;
          margin: 0;
        }
      `}
      {...props}
    />
  ) : (
    <p {...props} />
  )
}

// NOTE: Drop other attributes (allowtransparency, mozallowfullscreen, ...) to avoid errors from React.
const IFrame: React.FC<{ id?: string; src: string }> = (props) => (
  <iframe
    css={css`
      background-color: transparent;
      overflow: hidden;
      ${iframeCss(props.src)}
    `}
    id={props.id}
    src={props.src}
    frameBorder="0"
    allowFullScreen
  />
)

const iframeCss = (src: string) => {
  if (src.includes('//speakerdeck.com/player')) {
    // for SpeakerDeck
    return css`
      &&&& {
        margin: 20px 5px;
        width: calc(100% - 10px);
        height: calc(100vw * 0.562);

        ${mq.large} {
          margin: 20px 0;
          width: 100%;
          height: calc(770px * 0.562);
        }
      }
    `
  } else {
    // for Google Slide, Datawrapper (charts), etc.
    return css`
      &&&& {
        margin: 20px 0;
        width: 100%;
        height: auto;
        min-height: 350px;
      }
    `
  }
}

type HeadingProps = {
  children: React.ReactNode
}

const Heading: React.FC<HeadingProps> = (props) => (
  <h2
    css={css`
      position: relative;
    `}
  >
    <div
      id={`${props.children}`}
      css={css`
        position: absolute;
        top: -65px;
      `}
    />
    <span>{props.children}</span>
  </h2>
)

type LinkProps = {
  href: string
  children: React.ReactNode
}

const Link: React.FC<LinkProps> = (props) => {
  if (props.href.startsWith('#')) {
    return <WithinPageLink {...props} />
  }

  return <ExternalLink {...props} />
}

const WithinPageLink: React.FC<LinkProps> = (props) => {
  return (
    <AnchorLink
      href={props.href}
      className={isText(props.children) ? 'text-link' : undefined}
      css={linkCss()}
    >
      {props.children}
    </AnchorLink>
  )
}

export const ExternalLink: React.FC<LinkProps> = (props) => {
  return (
    <a
      href={props.href}
      target="_blank"
      className={isText(props.children) ? 'text-link' : undefined}
      css={linkCss()}
    >
      {props.children}
    </a>
  )
}

const isText = (node: React.ReactNode) =>
  Array.isArray(node) && typeof node[0] === 'string'

const linkCss = () => {
  return css`
    color: ${colors.link};
    overflow-wrap: break-word;
    padding-bottom: 1px;
    margin: 0 2px;

    &:hover {
      text-decoration: underline;
    }

    &:focus {
      outline: none;
    }
  `
}
